import {
  BaseAutoTracker,
  IAutoTrackerConfig,
  ICapturedEvent,
} from './BaseAutoTracker';
import { SelectorUtils } from './utils/SelectorUtils';
import { EventManager } from './utils/EventManager';
import { GomarkePixelConfig, PixelResponse } from '@/types';
import {
  IRemoveToCartData,
  RemoveToCartTracker,
} from '@/conversion/tracks/removeToCart';
import type { TrackConversionFn } from '../engine';

/**
 * Configuração específica para autoRemoveToCart
 */
export interface IAutoRemoveToCartConfig extends IAutoTrackerConfig {
  extractProductData?: boolean;
  productDataSelectors?: {
    currency?: string;
    quantity?: string;
    name?: string;
    price?: string;
    category?: string;
    id?: string;
    image?: string;
  };
  customDataExtractor?: (element: Element) => Record<string, unknown>;
}

/**
 * AutoTracker para eventos de remover do carrinho
 */
export class AutoRemoveToCartTracker extends BaseAutoTracker {
  private removeToCartTracker: RemoveToCartTracker;
  private eventManager: EventManager;
  private autoConfig?: IAutoRemoveToCartConfig;
  private trackConversion?: TrackConversionFn;

  constructor(
    config: GomarkePixelConfig,
    sessionId: string,
    version: string,
    visitorId: string = '',
    trackConversion?: TrackConversionFn
  ) {
    super(config, sessionId, version, visitorId);
    this.trackConversion = trackConversion;
    this.removeToCartTracker = new RemoveToCartTracker(
      config,
      sessionId,
      this.visitorId,
      version
    );
    this.eventManager = new EventManager({
      debounceMs: 300,
      throttleMs: 1000,
      enableLogging: config.debug,
    });
  }

  public async initialize(autoConfig: IAutoRemoveToCartConfig): Promise<void> {
    if (!autoConfig.isEnabled) {
      this.log('AutoRemoveToCart desabilitado');
      return;
    }

    this.autoConfig = autoConfig;
    this.log(
      `Inicializando AutoRemoveToCart com seletor: ${autoConfig.selector}`
    );

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () =>
        this.setupListeners()
      );
    } else {
      await this.setupListeners();
    }

    this.observeDOM(() => this.setupListeners());
    this.isInitialized = true;
  }

  private async setupListeners(): Promise<void> {
    if (!this.autoConfig) return;

    this.clearListeners();

    try {
      const elements = await SelectorUtils.findElementsWithRetry(
        this.autoConfig.selector,
        this.autoConfig.selectorType,
        3,
        1000
      );

      this.log(`Encontrados ${elements.length} elementos para RemoveToCart`);

      elements.forEach(element => {
        const htmlElement = element as Element;

        this.addListenerWithTiming(
          htmlElement,
          'click',
          this.handleRemoveToCart.bind(this),
          this.autoConfig!
        );

        this.addListenerWithTiming(
          htmlElement,
          'keydown',
          this.handleKeyboardRemoveToCart.bind(this),
          this.autoConfig!
        );
      });
    } catch (error) {
      this.log(`Erro ao configurar listeners: ${String(error)}`);
    }
  }

  private async handleRemoveToCart(
    capturedEvent: ICapturedEvent
  ): Promise<PixelResponse> {
    this.log('RemoveToCart detectado via clique', capturedEvent);

    const productData = this.extractProductData(capturedEvent.element);
    const itemId =
      productData.id ||
      this.generateItemId(capturedEvent.element) ||
      `item_${Date.now()}`;
    const itemName =
      productData.name ||
      this.extractTextContent(capturedEvent.element) ||
      'Produto';

    if (!itemId || !itemName) {
      return Promise.resolve({
        success: false,
        error: 'Dados do produto não encontrados',
        timestamp: Date.now(),
      });
    }

    const payload: IRemoveToCartData = {
      itemId,
      itemName,
      value: productData.price,
      currency: productData.currency || 'BRL',
    };

    if (this.trackConversion) {
      return this.trackConversion('removeToCart', payload);
    }
    return this.removeToCartTracker.track(payload);
  }

  private async handleKeyboardRemoveToCart(
    capturedEvent: ICapturedEvent
  ): Promise<PixelResponse> {
    const keyboardEvent = capturedEvent.event as KeyboardEvent;

    if (keyboardEvent.key !== 'Enter' && keyboardEvent.key !== ' ') {
      return Promise.resolve({
        success: false,
        error: 'Tecla não suportada',
        timestamp: Date.now(),
      });
    }

    this.log('RemoveToCart detectado via teclado', capturedEvent);
    return this.handleRemoveToCart(capturedEvent);
  }

  private extractProductData(element: Element): {
    id?: string;
    name?: string;
    price?: number;
    category?: string;
    quantity?: number;
    image?: string;
    currency?: string;
  } {
    if (!this.autoConfig?.productDataSelectors) {
      return {};
    }

    const data: Record<string, unknown> = {};

    if (this.autoConfig.productDataSelectors.id) {
      data.id = element.getAttribute(this.autoConfig.productDataSelectors.id);
    }
    if (this.autoConfig.productDataSelectors.name) {
      data.name = element.getAttribute(
        this.autoConfig.productDataSelectors.name
      );
    }
    if (this.autoConfig.productDataSelectors.price) {
      data.price = this.parsePrice(
        element.getAttribute(this.autoConfig.productDataSelectors.price) ??
          undefined
      );
    }
    if (this.autoConfig.productDataSelectors.category) {
      data.category = element.getAttribute(
        this.autoConfig.productDataSelectors.category
      );
    }
    if (this.autoConfig.productDataSelectors.image) {
      data.image = element.getAttribute(
        this.autoConfig.productDataSelectors.image
      );
    }
    if (this.autoConfig.productDataSelectors.quantity) {
      data.quantity = element.getAttribute(
        this.autoConfig.productDataSelectors.quantity
      );
    }
    if (this.autoConfig.productDataSelectors.currency) {
      data.currency = element.getAttribute(
        this.autoConfig.productDataSelectors.currency
      );
    }

    if (this.autoConfig.customDataExtractor) {
      const customData = this.autoConfig.customDataExtractor(element);
      Object.assign(data, customData);
    }

    return data;
  }

  private generateItemId(element: Element): string {
    const position = SelectorUtils.getElementPosition(element);
    return `item_${Date.now()}_${Math.floor(position.top)}_${Math.floor(position.left)}`;
  }

  private extractTextContent(element: Element): string {
    return (
      element.textContent?.trim() || element.getAttribute('title') || 'Produto'
    );
  }

  private parsePrice(priceText?: string): number | undefined {
    if (!priceText) return undefined;
    const cleanPrice = priceText.replace(/[^\d,.-]/g, '');
    const normalizedPrice = cleanPrice.replace(',', '.');
    const price = parseFloat(normalizedPrice);
    return isNaN(price) ? undefined : price;
  }

  public stop(): void {
    this.clearListeners();
    this.eventManager.clear();
    this.isInitialized = false;
    this.log('AutoRemoveToCart parado');
  }

  public async rebuild(): Promise<void> {
    if (this.autoConfig) {
      await this.setupListeners();
    }
  }

  public getRemoveToCartStats(): {
    baseStats: unknown;
    eventStats: unknown;
  } {
    return {
      baseStats: {},
      eventStats: this.eventManager.getStats(),
    };
  }

  private log(message: string, data?: unknown): void {
    if (this.baseConfig.debug) {
      console.log(`[AutoRemoveToCart] ${message}`, data);
    }
  }
}

// Função de conveniência para compatibilidade
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
export const autoRemoveToCart = (_config: IAutoRemoveToCartConfig): void => {
  console.warn(
    'autoRemoveToCart: Use a classe AutoRemoveToCartTracker diretamente'
  );
};
